/**
 * GuidanceNudgeBubble — la bulle de « coup de pouce » qui émane de l'orbe Juliette.
 *
 * Présentationnel : lit le conseil courant via `useGuidanceNudge()` et l'affiche
 * en bulle ancrée au-dessus de l'orbe (desktop = sidebar, mobile = bottom nav).
 * Un conseil peut comporter PLUSIEURS cartes (`steps`) : la bulle montre alors une
 * flèche « suivant » + des points de progression, et le CTA n'apparaît que sur la
 * dernière carte. Deux fermetures : « X » (Fermer → report) et « Ne plus voir »
 * (définitif).
 *
 * Doit être monté dans un conteneur `relative` (l'ancrage de l'orbe). Une seule
 * des deux instances (sidebar/mobile) est visible à la fois — l'autre est masquée
 * par les breakpoints CSS de son conteneur.
 */

import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronRight, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useGuidanceNudge } from "@/hooks/use-guidance-nudge";

interface GuidanceNudgeBubbleProps {
  placement: "sidebar" | "mobile";
}

export function GuidanceNudgeBubble({ placement }: GuidanceNudgeBubbleProps) {
  const { nudge, snooze, dismiss, runCta } = useGuidanceNudge();
  const [step, setStep] = useState(0);

  // Repart de la 1re carte à chaque nouveau conseil.
  useEffect(() => {
    setStep(0);
  }, [nudge?.key]);

  // Mobile : ancrée sur left-1/2, on RECENTRE via la prop `x` de framer-motion
  // (voir plus bas). NE PAS utiliser -translate-x-1/2 en classe Tailwind ici :
  // framer écrit un `transform` inline (translateY/scale) qui écraserait la classe
  // (inline > CSS) et ferait déborder la bulle à droite hors de l'écran.
  const positionClass =
    placement === "mobile"
      ? "absolute bottom-full left-1/2 mb-6 z-50 w-[min(18rem,calc(100vw-2rem))]"
      : "absolute bottom-full left-0 mb-2 z-50 w-64";

  // Décalage de recentrage horizontal, intégré aux keyframes framer-motion pour
  // rester cohérent avec le `transform` inline de l'animation.
  const centerX = placement === "mobile" ? "-50%" : 0;

  const tailClass =
    placement === "mobile"
      ? "absolute -bottom-1 left-1/2 -translate-x-1/2"
      : "absolute -bottom-1 left-5";

  const total = nudge?.steps.length ?? 0;
  const safeStep = Math.min(step, Math.max(0, total - 1));
  const isLast = safeStep >= total - 1;

  return (
    <AnimatePresence>
      {nudge && (
        <motion.div
          key={nudge.key}
          role="status"
          aria-live="polite"
          initial={{ opacity: 0, y: 8, scale: 0.96, x: centerX }}
          animate={{ opacity: 1, y: 0, scale: 1, x: centerX }}
          exit={{ opacity: 0, y: 8, scale: 0.96, x: centerX }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          className={positionClass}
        >
          <div className="relative rounded-2xl border border-primary/20 bg-popover p-3 pr-9 shadow-xl shadow-primary/10">
            {/* Pointe vers l'orbe */}
            <span
              aria-hidden
              className={cn(
                "h-3 w-3 rotate-45 border-b border-r border-primary/20 bg-popover",
                tailClass,
              )}
            />

            {/* Fermer (report) */}
            <button
              type="button"
              onClick={snooze}
              aria-label="Fermer ce conseil"
              className="absolute right-1.5 top-1.5 flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>

            <div className="flex items-center gap-1.5">
              <Sparkles className="h-3.5 w-3.5 text-primary" />
              <span className="font-juliette text-xs font-semibold text-primary">
                Juliette
              </span>
            </div>

            <p
              key={safeStep}
              className="mt-1.5 text-sm leading-snug text-popover-foreground animate-in fade-in duration-200"
            >
              {nudge.steps[safeStep]}
            </p>

            <div className="mt-2.5 flex items-center justify-between gap-2">
              {/* Progression (si plusieurs cartes) */}
              {total > 1 ? (
                <div className="flex items-center gap-1" aria-hidden>
                  {nudge.steps.map((_, i) => (
                    <span
                      key={i}
                      className={cn(
                        "h-1.5 w-1.5 rounded-full transition-colors",
                        i === safeStep ? "bg-primary" : "bg-muted-foreground/30",
                      )}
                    />
                  ))}
                </div>
              ) : (
                <span />
              )}
              {total > 1 && (
                <span className="sr-only">
                  Carte {safeStep + 1} sur {total}
                </span>
              )}

              <div className="flex items-center gap-2.5">
                <button
                  type="button"
                  onClick={dismiss}
                  className="shrink-0 text-2xs text-muted-foreground underline-offset-2 transition-colors hover:text-foreground hover:underline"
                >
                  Ne plus voir
                </button>

                {!isLast ? (
                  <Button
                    size="sm"
                    className="h-8 gap-1 pl-3 pr-2 text-xs"
                    onClick={() => setStep((s) => Math.min(s + 1, total - 1))}
                    aria-label="Conseil suivant"
                  >
                    Suivant
                    <ChevronRight className="h-3.5 w-3.5" />
                  </Button>
                ) : (
                  nudge.cta && (
                    <Button size="sm" className="h-8 px-3 text-xs" onClick={runCta}>
                      {nudge.cta.label}
                    </Button>
                  )
                )}
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
